(() => {
  if (window.MMCart) return;

  // Sections that only exist on the full /cart page. When a customer adds a
  // refill from the upsell while standing on /cart, the page behind the drawer
  // has to be re-rendered by Shopify so its prices, discounts and totals stay
  // authoritative.
  const PAGE_SECTIONS = ['cart-items', 'cart-footer', 'cart-upsell'];

  const pageSections = () =>
    PAGE_SECTIONS.map((name) => {
      const wrapper =
        document.querySelector(`[id^="shopify-section-"][id$="__${name}"]`) ||
        document.getElementById(`shopify-section-${name}`);
      return wrapper?.id.replace('shopify-section-', '');
    }).filter(Boolean);

  const sectionList = () => ['mm-cart-drawer', 'cart-icon-bubble'].concat(pageSections()).join(',');

  const syncPage = (sections) => {
    if (!sections) return;
    pageSections().forEach((id) => {
      const html = sections[id];
      if (typeof html !== 'string') return;
      const host = document.getElementById(`shopify-section-${id}`);
      if (!host) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(`shopify-section-${id}`);
      if (fresh) host.innerHTML = fresh.innerHTML;
    });
  };

  window.MMCart = { sectionList, syncPage };

  // EasySell reads the cart when its form starts. Reload after a saved cart
  // change so the app, upsell, and visible totals all use the same contents.
  const watchCartPage = () => {
    if (!document.querySelector('cart-items')) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') window.location.reload();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartPage);
  } else {
    watchCartPage();
  }

  // Shopify's checkout button and the COD app's button were both showing on
  // /cart. Hiding the native one outright is exactly what left the store
  // unable to take an order when the previous COD app silently stopped
  // injecting, so this is conditional and self-healing: the flag is only set
  // while the COD button is genuinely on the page, and it is removed the
  // moment that button goes away.
  const watchCodButton = () => {
    const root = document.documentElement;
    const sync = () => {
      const es = document.getElementById('es-popup-button');
      root.classList.toggle('mm-cod-live', !!es && es.offsetParent !== null);
    };
    sync();
    if (!document.body) return;
    new MutationObserver(sync).observe(document.body, { childList: true, subtree: true });
    // The app renders after its own script boots, which can be after ours.
    window.setTimeout(sync, 1500);
    window.setTimeout(sync, 4000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCodButton);
  } else {
    watchCodButton();
  }

  const showCodTotal = () => {
    if (!document.querySelector('cart-items')) return;

    let currentForm;
    let formObserver;
    let subtotalCents;

    const money = (cents) => `${new Intl.NumberFormat('fr-MA', { maximumFractionDigits: 2 }).format(cents / 100)} DH`;

    const sync = () => {
      if (!currentForm || !Number.isFinite(subtotalCents)) return;
      const shippingGroup = currentForm.querySelector('.es-shipping_options-group');
      const selected = shippingGroup?.querySelector('.es-selected-shipping-line');
      const priceText = selected?.querySelector('.shipping-price')?.textContent.trim();
      if (!priceText) return;

      const amount = priceText.replace(/\s/g, '').match(/\d+(?:[.,]\d{1,2})?/);
      const isFree = /offerte|gratuit|free|مجاني/i.test(priceText);
      if (!amount && !isFree) return;
      const shippingCents = amount ? Math.round(Number(amount[0].replace(',', '.')) * 100) : 0;
      if (!Number.isFinite(shippingCents)) return;

      let summary = shippingGroup.querySelector('[data-mm-cod-summary]');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'mm-cod-summary';
        summary.dataset.mmCodSummary = '';
        summary.setAttribute('role', 'status');
        summary.setAttribute('aria-live', 'polite');
        shippingGroup.appendChild(summary);
      }

      const content = `<div class="mm-cod-summary__row"><span>Sous-total produits</span><strong>${money(subtotalCents)}</strong></div><div class="mm-cod-summary__row"><span>Livraison</span><strong>${money(shippingCents)}</strong></div><div class="mm-cod-summary__row mm-cod-summary__row--total"><span>Total à payer à la livraison</span><strong>${money(subtotalCents + shippingCents)}</strong></div>`;
      if (summary.innerHTML !== content) summary.innerHTML = content;
    };

    const attach = () => {
      const form = document.getElementById('es-form');
      if (!form || form === currentForm) return;
      if (formObserver) formObserver.disconnect();
      currentForm = form;
      subtotalCents = undefined;
      formObserver = new MutationObserver(sync);
      formObserver.observe(form, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });

      fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .then((cart) => {
          if (currentForm !== form) return;
          subtotalCents = cart.total_price;
          sync();
        })
        .catch(() => {});
    };

    new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });
    attach();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showCodTotal);
  } else {
    showCodTotal();
  }

  const drawer = () => document.querySelector('mm-cart-drawer');

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-mm-open-cart]');
    if (!opener) return;
    const d = drawer();
    if (!d) return;
    event.preventDefault();
    d.open(opener);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-mm-atc]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const feedback = form.querySelector('[data-mm-atc-feedback]');
    if (button) button.setAttribute('aria-disabled', 'true');
    if (feedback) feedback.hidden = true;

    const body = new FormData(form);
    body.append('sections', sectionList());
    body.append('sections_url', window.location.pathname);

    fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || (data.status && data.status !== 200)) {
          if (feedback) {
            feedback.textContent = data.description || data.message || 'Ajout impossible pour le moment.';
            feedback.hidden = false;
          }
          return;
        }
        syncPage(data.sections);
        const d = drawer();
        if (d && data.sections) {
          d.render(data.sections);
          d.open(button);
        } else if (d) {
          d.refresh().then(() => d.open(button));
        } else {
          window.location.href = window.routes.cart_url;
        }
      })
      .catch(() => {
        if (feedback) {
          feedback.textContent = 'Ajout impossible pour le moment. Réessayez dans un instant.';
          feedback.hidden = false;
        }
      })
      .finally(() => {
        if (button) button.removeAttribute('aria-disabled');
      });
  });
})();
